import { supabase, funzioneUrl } from './supabase'

/**
 * COMPLETAMENTO DELL'ISCRIZIONE
 *
 * Tutto ciò che va fatto DOPO che un nuovo utente ha un account attivo:
 * email di benvenuto, codice referral, abbonamento regalo.
 *
 * Perché un modulo a sé: queste operazioni richiedono una sessione attiva.
 * Finché la conferma email è disattivata, la sessione esiste già al termine
 * della registrazione e si eseguono subito. Attivando la conferma, invece, la
 * registrazione NON produce una sessione: l'utente la ottiene solo dopo aver
 * cliccato il link ricevuto per email, e solo allora queste operazioni possono
 * andare a buon fine. Tenendole in un posto solo, funzionano in entrambi i casi
 * senza duplicare la logica in due punti che poi divergono.
 *
 * I parametri (invito, referral, regalo) viaggiano nell'indirizzo del link di
 * conferma, non nella memoria del browser: così sopravvivono anche se la posta
 * viene aperta sul telefono mentre la registrazione era partita dal computer.
 */

/** Legge i parametri dell'iscrizione dall'indirizzo corrente. */
export function parametriIscrizione(search = window.location.search) {
  const p = new URLSearchParams(search)
  let invitoSalvato = null
  // App.js toglie il token dall'indirizzo appena lo legge (per non lasciarlo nella
  // barra o nei preferiti) e lo mette da parte qui: senza questo recupero, al momento
  // della registrazione il token risulterebbe assente e il link di conferma non lo
  // porterebbe con sé, facendo perdere l'adesione al gruppo.
  try { invitoSalvato = sessionStorage.getItem('fp_invito') } catch { /* modalità senza memoria */ }
  return {
    invito: p.get('invito') || invitoSalvato || null,
    referral: p.get('ref') || null,
    regalo: p.get('gift') || null,
  }
}

/** Costruisce l'indirizzo a cui riportare l'utente dopo la conferma email,
 *  conservando i parametri con cui era arrivato. */
export function indirizzoDopoConferma(parametri = parametriIscrizione()) {
  const q = new URLSearchParams()
  if (parametri.invito) q.set('invito', parametri.invito)
  if (parametri.referral) q.set('ref', parametri.referral)
  if (parametri.regalo) q.set('gift', parametri.regalo)
  const coda = q.toString()
  return window.location.origin + (coda ? `/?${coda}` : '/')
}

async function applicaReferral(utenteId, codice) {
  if (!codice) return
  const { data: rc } = await supabase.from('referral_codes')
    .select('utente_id').eq('codice', codice).maybeSingle()
  if (!rc?.utente_id) return
  await supabase.from('profili').update({ referral_da: rc.utente_id }).eq('id', utenteId)
  await supabase.rpc('incrementa_referral', { codice_ref: codice })
}

async function applicaRegalo(utenteId, token) {
  if (!token) return
  const { data: gift } = await supabase.from('gift_tokens')
    .select('*').eq('token', token).eq('stato', 'pending').maybeSingle()
  if (!gift || new Date(gift.scadenza_token) <= new Date()) return

  const scadenza = new Date()
  scadenza.setMonth(scadenza.getMonth() + gift.mesi)
  await supabase.from('profili').update({
    piano: gift.piano,
    abbonamento_attivo: true,
    regalo: true,
    regalo_da: gift.mittente_email,
    abbonamento_dal: new Date().toISOString(),
    abbonamento_al: scadenza.toISOString(),
  }).eq('id', utenteId)
  await supabase.from('gift_tokens').update({
    stato: 'riscattato',
    riscattato_il: new Date().toISOString(),
    riscattato_da: utenteId,
  }).eq('token', token)
  await supabase.from('notifiche').insert({
    utente_id: utenteId,
    titolo: '🎁 Abbonamento regalo attivato!',
    testo: `Hai ricevuto un abbonamento ${gift.piano} di ${gift.mesi} ${gift.mesi === 1 ? 'mese' : 'mesi'} in regalo. Scadenza: ${scadenza.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}.`,
    tipo: 'regalo',
  })
}

async function inviaBenvenuto(utenteId, accessToken) {
  // Il segno viene messo con la stessa istruzione che lo verifica: se la riga
  // torna aggiornata, siamo gli unici ad averlo fatto e l'email parte una volta
  // sola, anche con due schede aperte o un doppio ritorno dal link di conferma.
  const { data: vinta } = await supabase.from('profili')
    .update({ benvenuto_inviato: true })
    .eq('id', utenteId).eq('benvenuto_inviato', false)
    .select('email, nome')
  const profilo = vinta?.[0]
  if (!profilo || !accessToken) return
  // "Fire and forget": un disservizio dell'email non deve bloccare l'ingresso nell'app
  fetch(funzioneUrl('send-gift-email'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ tipo: 'benvenuto', destinatario_email: profilo.email, destinatario_nome: profilo.nome }),
  }).catch(() => {})
}

/**
 * Esegue i passi mancanti dell'iscrizione. Sicura da richiamare più volte:
 * ogni passo è protetto contro i doppioni.
 *
 * Non riceve il profilo dall'app di proposito: quello caricato in memoria non
 * contiene tutti i campi che servono qui, e basterebbe cambiare la lista dei
 * campi letti altrove per rompere il controllo anti-doppione senza accorgersene.
 */
export async function completaIscrizione(parametri = parametriIscrizione()) {
  const { data: { session } } = await supabase.auth.getSession()
  const utenteId = session?.user?.id
  if (!utenteId) return

  try { await applicaReferral(utenteId, parametri.referral) } catch { /* non blocca gli altri passi */ }
  try { await applicaRegalo(utenteId, parametri.regalo) } catch { /* idem */ }
  try { await inviaBenvenuto(utenteId, session.access_token) } catch { /* idem */ }
  // L'invito al gruppo resta gestito in App.js, che ne mostra anche l'esito a schermo
}
